import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchSentenceTableDataValidatorAdmin } from '@store/thunks/validatorAdminThunk';
import GenericTable from "@components/tables/GenericTable";
import { BASE_URL, getStatusStyle } from "@utils/constants";
import apiClient from "@/services/api/apiClient";

const SentenceTableValidatorAdmin = () => {
  const dispatch = useDispatch();
  const { fileId, sourceId, targetId } = useParams();
  const validatorAdminId = useSelector((state) => state.auth?.userId);
  const token = useSelector((state) => state.auth?.token);
  const { fileName } = useSelector((state) => state.fileMeta);
    const { targetLanguageName } = useSelector((state) => state.fileLanguage);
  const [allSentences, setAllSentences] = useState([]);
  const [rejectedSentences, setRejectedSentences] = useState([]);
  const [clickReject, setClickReject] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingRejected, setLoadingRejected] = useState(false);
  const [rejectedError, setRejectedError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0); // Total entries for active tab
  const [hasNextPage, setHasNextPage] = useState(true);
  const navigate=useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!fileId || !sourceId || !targetId || !validatorAdminId) {
        console.warn("[Fetch Data] Missing parameters:", { fileId, sourceId, targetId, validatorAdminId });
        setLoading(false);
        setLoadingRejected(false);
        setRejectedError("Missing required parameters to fetch sentences.");
        return;
      }

      if (clickReject) {
        // Fetch rejected sentences
        setLoadingRejected(true);
        setRejectedError("");
        console.log("[Rejected Sentences] Fetching with:", { fileId, sourceId, targetId, validatorAdminId });
        try {
          const data = await apiClient(
            `${BASE_URL}api/validatorAdmin/rejectedSentences?fileId=${fileId}&sourceId=${sourceId}&targetId=${targetId}&validatorAdminId=${validatorAdminId}`,
            {
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              },
            },navigate
          );
          console.log("[Rejected Sentences] API response:", data.data);
          if (data.status === "success" && Array.isArray(data.data)) {
            setRejectedSentences(data.data);
            setTotalEntries(data.data.length); // Fallback to data length if totalCount is missing
            setPageIndex(0); // Reset to first page
          } else {
            setRejectedSentences([]);
            setTotalEntries(0);
            setRejectedError(data.message || "Failed to fetch rejected sentences.");
          }
        } catch (err) {
          setRejectedSentences([]);
          setTotalEntries(0);
          setRejectedError("Failed to fetch rejected sentences. Check console for details.");
          console.error("[Rejected Sentences] Fetch error:", err);
        }
        setLoadingRejected(false);
      } else {
        // Fetch all sentences
        setLoading(true);
        try {
          console.log("[All Sentences] Fetching with:", { fileId, sourceId, targetId, validatorAdminId });
          const res = await dispatch(fetchSentenceTableDataValidatorAdmin({
            fileId,
            sourceId,
            targetId,
            validatorAdminId,
          }));
          console.log("[All Sentences] API response:",res);
          if (Array.isArray(res)) {
            setAllSentences(res);
            setTotalEntries(res.length); // Fallback to data length if totalCount is missing
            setPageIndex(0); // Reset to first page
          } else {
            setAllSentences([]);
            setTotalEntries(0);
          }
        } catch (err) {
          console.error("[All Sentences] Fetch error:", err);
          setAllSentences([]);
          setTotalEntries(0);
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [fileId, sourceId, targetId, validatorAdminId, clickReject, dispatch]);

  // Client-side pagination
  const paginatedSentences = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return allSentences.slice(start, end);
  }, [allSentences, pageIndex, pageSize]);

  const paginatedRejectedSentences = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return rejectedSentences.slice(start, end);
  }, [rejectedSentences, pageIndex, pageSize]);

  // Update hasNextPage based on totalEntries
  useEffect(() => {
    const activeData = clickReject ? rejectedSentences : allSentences;
    setHasNextPage((pageIndex + 1) * pageSize < activeData.length);
  }, [allSentences, rejectedSentences, clickReject, pageIndex, pageSize]);

  const allSentencesColumns = [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row }) => (
        <div className="text-center">
          {pageIndex * pageSize + row.index + 1}
        </div>
      ),
      cellClassName: "text-center",
    },
    {
      id: "sentenceText",
      header: () => "CDAC Sentence Text",
      accessorKey: "cdacSentenceText",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.cdacSentenceText || "N/A"}</div>
      ),
    },
    {
      id: "translatedTextGs",
      header: () => `GS Translated Text (${targetLanguageName})`,
      accessorKey: "translatedTextGs",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.translatedTextGs || "N/A"}</div>
      ),
    },
    {
      id: "validatedTextGs",
      header: () => `GS Validated Text (${targetLanguageName})`,
      accessorKey: "validatedTextGs",
      cell: ({ row }) => (
        <div className="max-w-md">
          {row.original.validatedTextGs === 'validated by the ga validator (2 Phase Translation)'
            ? row.original.translatedTextGs || "N/A"
            : (row.original.validationStatus === 'PENDING' ? 'not yet validated' : row.original.validatedTextGs || "N/A")}
        </div>
      ),
    },
    {
      id: "translationStatus",
      header: () => "Translation Status",
      accessorKey: "translationStatus",
      cell: ({ row }) => (
        <span className={`px-2 py-1 rounded-full text-sm font-['Semantic/Strong'] ${getStatusStyle(row.original.translationStatus).bg} ${getStatusStyle(row.original.translationStatus).text}`}>
          {row.original.translationStatus || "N/A"}
        </span>
      ),
    },
    {
      id: "validationStatus",
      header: () => "Validation Status",
      accessorKey: "validationStatus",
      cell: ({ row }) => (
        <span className={`px-2 py-1 rounded-full text-sm font-['Semantic/Strong'] ${getStatusStyle(row.original.validationStatus).bg} ${getStatusStyle(row.original.validationStatus).text}`}>
          {row.original.validationStatus || "N/A"}
        </span>
      ),
    },
  ];

  const rejectedSentencesColumns = [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row }) => (
        <div className="text-center">
          {pageIndex * pageSize + row.index + 1}
        </div>
      ),
      cellClassName: "text-center",
    },
    {
      id: "sourceText",
      header: () => "CDAC Source Text",
      accessorKey: "cdacSentenceText",
      cell: ({ row }) => (
        <div className="max-w-md font-['Semantic/Regular']">{row.original.cdacSentenceText || "N/A"}</div>
      ),
    },
    {
      id: "translatedText",
      header: () => `GS Translated Text (${targetLanguageName})`,
      accessorKey: "translatedTextGs",
      cell: ({ row }) => (
        <div className="max-w-md font-['Semantic/Regular']">{row.original.translatedTextGs || "N/A"}</div>
      ),
    },
    {
      id: "translationStatus",
      header: () => "Translation Status",
      accessorKey: "translationStatus",
      cell: ({ row }) => (
        <span className={`px-2 py-1 rounded-full text-sm font-['Semantic/Strong'] ${getStatusStyle(row.original.translationStatus).bg} ${getStatusStyle(row.original.translationStatus).text}`}>
          {row.original.translationStatus || "N/A"}
        </span>
      ),
    },
    {
      id: "validationStatus",
      header: () => "Validation Status",
      accessorKey: "validationStatus",
      cell: ({ row }) => (
        <span className={`px-2 py-1 rounded-full text-sm font-['Semantic/Strong'] ${getStatusStyle(row.original.validationStatus).bg} ${getStatusStyle(row.original.validationStatus).text}`}>
          {row.original.validationStatus || "N/A"}
        </span>
      ),
    },
  ];

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="flex-1">
        <div className="w-full p-4 sm:p-6 lg:p-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
             <h2 className="text-xl font-['Semantic/Strong'] text-center font-bold mt-2">
              File Name: <span className="text-2xl font-['Semantic/Strong'] text-center text-[#ec7026] underline font-bold">{fileName}</span>
            </h2>
            <div className="px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center mb-4">
                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${!clickReject
                      ? "bg-[#ec7026] text-white"
                      : "bg-white text-[#ec7026] hover:bg-gray-50"
                      } transition-colors duration-200`}
                    onClick={() => {
                      setClickReject(false);
                      setPageIndex(0); // Reset to first page
                    }}
                  >
                    All Sentences
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${clickReject
                      ? "bg-[#ec7026] text-white"
                      : "bg-white text-[#ec7026] hover:bg-gray-50"
                      } transition-colors duration-200`}
                    onClick={() => {
                      setClickReject(true);
                      setPageIndex(0); // Reset to first page
                    }}
                  >
                    Rejected Sentences
                  </button>
                </div>
              </div>
              {clickReject ? (
                loadingRejected ? (
                  <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                  </div>
                ) : rejectedError ? (
                  <div className="p-6">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg font-['Semantic/Strong']">
                      <p>{rejectedError}</p>
                      <p className="mt-2 text-sm">Please try again later or contact support.</p>
                    </div>
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto">
                    <GenericTable
                      data={paginatedRejectedSentences}
                      columns={rejectedSentencesColumns}
                      showPagination={true}
                      manualPagination={true}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      hasNextPage={hasNextPage}
                      onPaginationChange={({ pageIndex, pageSize }) => {
                        setPageIndex(pageIndex);
                        setPageSize(pageSize);
                      }}
                      emptyMessage="No rejected sentences"
                    />
                  </div>
                )
              ) : loading ? (
                <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                  <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                  <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                  <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                </div>
              ) : (
                <div className="w-full overflow-x-auto">
                  <GenericTable
                    data={paginatedSentences}
                    columns={allSentencesColumns}
                    showPagination={true}
                    manualPagination={true}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    hasNextPage={hasNextPage}
                    onPaginationChange={({ pageIndex, pageSize }) => {
                      setPageIndex(pageIndex);
                      setPageSize(pageSize);
                    }}
                    emptyMessage="No sentences available"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SentenceTableValidatorAdmin;